import { FormSectionHandler } from '../entity.handler';
import { ScheduledReportResponse, ScheduledReportUpdateRequest, IdName, Schedule, IdNameType } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField, IdNameComboValueHandlers } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, ComboField, DateTimeField, NumberField, CheckListField, TimeField } from '../utils';
import { ICONS } from '../icons';
import moment from 'moment-timezone';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import { DAYS_OF_THE_WEEK, MONTHS_OF_THE_YEAR } from './utils';
import { nullAccessDenied } from 'app/services';

interface EditorValues {
    name?: string;
    user?: IdName;
    startTime?: string;
    scheduleType?: string;
    time?: string;
    every?: number;
    days?: string[];
    day?: number;
    months?: string[];
    recipients?: ListFieldValue<IdNameType>[];
}

export class ScheduledReportDetailsSectionHandler implements FormSectionHandler<ScheduledReportResponse, EditorValues> {

    type = 'form';
    icon = ICONS.scheduledreport;
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    scheduleType: Schedule['scheduleType'];

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public creating: boolean
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.SCHEDULEDREPORT.DETAILS.${key}`, values);
    }

    applyUpdate(entity: ScheduledReportResponse, payload: ScheduledReportUpdateRequest): Promise<ScheduledReportResponse> {
        return this.admin.applyUpdate(api => api.entities.updateScheduledReport(entity.id, payload), { _type: 'scheduledreport' });
    }

    async mapEntityToEditor(entity: ScheduledReportResponse): Promise<EditorValues> {
        this.scheduleType = entity.schedule.scheduleType;
        const result = {
            name: entity.name,
            user: entity.user,
            startTime: entity.schedule.startTime,
            time: moment(entity.schedule.startTime).format('HH:mm:ss'),
            scheduleType: entity.schedule.scheduleType,
            every: entity.schedule.every || 1,
            days: Array.from(new Set((entity.schedule.days || [0]).map(x => x.toString()))),
            day: (entity.schedule.days && entity.schedule.days[0]) || 1,
            months: Array.from(new Set((entity.schedule.months || [1]).map(x => x.toString()))),
            recipients: (entity.recipients || []).map(x => this.recipientToListItem(x)),
        };
        return result;
    }

    async mapEditorToEntity(entity: ScheduledReportUpdateRequest, values: EditorValues): Promise<ScheduledReportUpdateRequest> {

        let days = values.days;

        const scheduleType = values.scheduleType || this.scheduleType;

        if (scheduleType === 'monthly' && values.day) {
            days = [values.day.toString()];
        }
        
        const schedule: Schedule = {
            scheduleType: values.scheduleType as Schedule['scheduleType'],
            days: days?.map(x => parseFloat(x)),
            every: values.every,
            months: values.months?.map(x => parseFloat(x)),
        };

        if (scheduleType === 'once' && values.startTime) {
            schedule.startTime = typeof values.startTime === 'string' ? values.startTime : (values.startTime as any).toISOString(); // convert startTime to iso string if it is a Moment object
        }

        if (scheduleType === 'hourly') {
            schedule.startTime = this.getStartOfDay().utc().toISOString();
        }

        if (values.time) {
            const time = moment(values.time, 'HH:mm:ss').utc();
            if (scheduleType !== 'once') { // startTime for once off schedule should be exactly what the user seleted it to be
                schedule.startTime = this.getStartOfDay().set({ 'hour': time.get('hour'), 'minute': time.get('minute'), 'second': time.get('second') }).toISOString();
            }
        }
        Object.keys(schedule).forEach(key => schedule[key] === undefined && delete schedule[key]); // strip undefined properties from the object

        entity.name = values.name || entity.name;
        entity.user = values.user || entity.user;
        entity.recipients = (values.recipients && values.recipients.map(x => x.data)) || entity.recipients;
        entity.schedule = {
            ...entity.schedule,
            ...schedule,
        };
        Object.keys(entity).forEach(key => entity[key] === undefined && delete entity[key]); // strip undefined properties from the object
        return entity;

    }

    async getFields(entity: ScheduledReportResponse): Promise<FormBuilderField[]> {


        const [users, userRoles] = await Promise.all([
            // Pushed up limit to 1000 for now as you can't set 0 as a limit, think the api sees 0 as a falsy value and instead returns 100 as the limit.
            this.admin.getCached(`listUsers/${this.ownerId}/0/1000/name/state=active`, api => api.accounts.listUsers(this.ownerId, 0, 1000, 'name', 'state=active')).catch(nullAccessDenied),
            this.admin.getCached(`listUserRoles/${this.ownerId}/0/100/name`, api => api.accounts.listUserRoles(this.ownerId, 0, 100, 'name')).then(result => result.items).catch(nullAccessDenied),
        ]);

        const userValues = (users && users.items || []).map(x => ({ key: x.id, value: x.name }));
        if (!userValues.find(x => x.key === this.admin.user.id)) {
            userValues.unshift({ key: this.admin.user.id, value: this.admin.user.name });
        }

        const recipients = [
            { id: 'user', name: this.admin.i18n.instant('ADMIN.ENTITIES.USER'), children: userValues.map(user => ({ id: `user.${user.key}`, name: user.value })), keepOpen: true },
            userRoles && { id: 'role', name: this.admin.i18n.instant('ADMIN.ENTITIES.USERROLE'), children: (userRoles || []).map(role => ({ id: `role.${role.id}`, name: role.name })), keepOpen: true },
            !this.creating && { id: 'email', name: this.admin.i18n.instant('ADMIN.EDITORS.SCHEDULEDREPORT.EMAIL_MODAL.FORM.EMAIL'), type: 'email', keepOpen: false },
        ].filter(x => x);

        const formatDateTime = (field, values) => this.admin.units.format(values[field.id], 'date', 'long').format + ' ' + this.admin.units.format(values[field.id], 'time', 'short').format;
        const formatTime = (field, values) => this.admin.units.format(moment(values[field.id], 'HH:mm:ss').toISOString(), 'time', 'short').format;


        const fields: FormBuilderField[] = [
            TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
            ComboField('user', this.translate('FIELDS.RUN_AS'), true, userValues, IdNameComboValueHandlers),
            ComboField('scheduleType', this.translate('FIELDS.RUN'), true, [
                {
                    key: 'once', value: this.translate('SCHEDULE_TYPES.ONCE'), fields: [
                        { ...DateTimeField('startTime', this.translate('FIELDS.ON'), true), getText: formatDateTime },
                    ],
                },
                {
                    key: 'hourly', value: this.translate('SCHEDULE_TYPES.HOURLY'), fields: [
                        NumberField('every', this.translate('FIELDS.EVERY'), true, 1, 23, this.translate('UNITS.HOURS')),
                    ],
                },
                {
                    key: 'daily', value: this.translate('SCHEDULE_TYPES.DAILY'), fields: [
                        NumberField('every', this.translate('FIELDS.EVERY'), true, 1, 23, this.translate('UNITS.DAYS')),
                        { ...TimeField('time', this.translate('FIELDS.AT'), true), getText: formatTime },
                    ],
                },
                {
                    key: 'weekly', value: this.translate('SCHEDULE_TYPES.WEEKLY'), fields: [
                        {
                            ...CheckListField('days', this.translate('FIELDS.DAYS'), true, DAYS_OF_THE_WEEK.map((key, index) => ({ key: index.toString(), value: this.admin.i18n.instant(`SHARED.WEEK_DAYS.LONG.${key}`) }))),
                            getText: (field, values) => (values[field.id] || []).map(x => field.values.find(bv => bv.key === x).value),
                        },
                        { ...TimeField('time', this.translate('FIELDS.AT'), true), getText: formatTime },
                    ],
                },
                {
                    key: 'monthly', value: this.translate('SCHEDULE_TYPES.MONTHLY'), fields: [
                        NumberField('day', this.translate('FIELDS.DAY'), true, 1, 31, null),
                        {
                            ...CheckListField('months', this.translate('FIELDS.MONTHS'), true, MONTHS_OF_THE_YEAR.map((key, index) => ({ key: (++index).toString(), value: this.admin.i18n.instant(`SHARED.MONTHS.LONG.${key}`) }))),
                            getText: (field, values) => (values[field.id] || []).map(x => field.values.find(bv => bv.key === x).value),
                        },
                        { ...TimeField('time', this.translate('FIELDS.AT'), true), getText: formatTime },
                    ],
                },
            ]),
            {
                id: 'recipients', title: this.translate('FIELDS.RECIPIENTS'), type: 'list', required: false,
                actions: [{
                    name: this.admin.i18n.instant('SHARED.ADD'), dropdownValues: recipients, click: async (compositeId, _field, values) => {
                        const [type, id] = compositeId.split('.');
                        
                        if (type === 'email') {
                            
                            const result = await this.admin.showFormModal({
                                groups: [{
                                    name: this.admin.i18n.instant('ADMIN.EDITORS.SCHEDULEDREPORT.EMAIL_MODAL.TITLE'),
                                    fields: [
                                        { id: 'name', type: 'text', title: this.admin.i18n.instant('ADMIN.EDITORS.SCHEDULEDREPORT.EMAIL_MODAL.FORM.NAME'), required: true, max: 250, min: 1 },
                                        { id: 'email', type: 'text', title: this.admin.i18n.instant('ADMIN.EDITORS.SCHEDULEDREPORT.EMAIL_MODAL.FORM.EMAIL'), required: true, validate: (_f, _values, v) => {
                                            const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                                            const isValidEmail =  re.test(String(v).toLowerCase());
                                             if (!isValidEmail) {
                                                 return this.admin.i18n.instant('ADMIN.EDITORS.SCHEDULEDREPORT.EMAIL_MODAL.ERROR_EMAIL_FORMAT');
                                             } else {
                                                 return undefined;
                                             }
                                        }, max: 250, min: 1 },
                                    ],
                                }],
                              }, {name: '', email: ''});
                            if (result) {
                                if (!values.recipients.find(x => x.id === result.email)) {
                                    values.recipients.push(this.recipientToListItem({ id: result.email, type: type, name: result.name }));
                                }
                            }
                        } else {
                            if (id) {
                                const name = type === 'user'
                                    ? userValues.find(x => x.key === id).value
                                    : userRoles.find(x => x.id === id).name;
                                if (!values.recipients.find(x => x.id === id)) {
                                    values.recipients.push(this.recipientToListItem({ id, type, name }));
                                }
                            } 
                        }
                    },
                }],
                getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
            },

        ];

        return EntityUtils.prepareBulk(!entity, [], fields);

    }


    recipientToListItem(item: IdNameType): ListFieldValue<IdNameType> {
        const recipientType = {
            'role': this.admin.i18n.instant('ADMIN.ENTITIES.USERROLE'),
            'user': this.admin.i18n.instant('ADMIN.ENTITIES.USER'),
        };

        const recipientIcons = {
            'user': ICONS.user,
            'email': ICONS.email,
            'role': ICONS.userrole,
        };
        return {
            id: item.id,
            name: item.name,
            icon: recipientIcons[item.type],
            data: item,
            description: item.type === 'email' ? item.id : recipientType[item.type],
        };
    }

    // here to help with testing
    getStartOfDay(): moment.Moment {
        return moment().startOf('day').utc();
    }


}
